import { useEffect, useState } from 'react'
import type { Dispatch, MutableRefObject, SetStateAction } from 'react'
import type { ExplicitInputError } from './types'

type MaybeHTMLInputElement = HTMLInputElement | null
type HookProps = Readonly<{
  explicitError?: ExplicitInputError
  onValidate?: (valid: boolean) => void
  patternExplanation?: string
  /**
   * Useful when `useValidityState` is used within a managed component, and the
   * state for 'has this been modified?' is controlled at a higher level. Also
   * useful for tests.
   */
  previouslyModified?: boolean
  // @note Per `@types/react`, `ref`s are only considered mutable if the type
  //   includes `| null`.
  ref?: MutableRefObject<MaybeHTMLInputElement>
}>
type HookObject = Readonly<{
  errorMessage: string
  modified: boolean
  setCustomErrorMessage: Dispatch<SetStateAction<string>>
  setModified: Dispatch<SetStateAction<boolean>>
  setValid: Dispatch<SetStateAction<boolean>>
}>

/**
 * Custom error text to help users provide valid input. The component manually
 * renders the error text, rather than using the browser's built-in
 * presentation, so the text has to be manually specified.
 */
function errorMessageFromValidityState({
  node,
  patternExplanation,
}: {
  node: MaybeHTMLInputElement | undefined
  patternExplanation: string | undefined
}): string | null {
  const validityState = node?.validity
  if (!validityState) return null
  if (validityState.valid) return null

  let automaticErrorMessage = 'Input is invalid'

  // @note These are ordered by what will be most helpful to the user.
  //   For example, `valueMissing` is more relevant than `tooShort`.
  if (validityState.valueMissing) {
    automaticErrorMessage = 'This field is required'
  } else if (validityState.tooShort && node) {
    automaticErrorMessage = `Input must be at least ${
      node.getAttribute('minLength') || 0
    } characters`
  } else if (validityState.tooLong && node) {
    automaticErrorMessage = `Input must be at most ${
      node.getAttribute('maxLength') || 0
    } characters`
  } else if (validityState.patternMismatch) {
    automaticErrorMessage =
      typeof patternExplanation !== 'undefined'
        ? patternExplanation
        : 'Input must match the required format'
  } else if (validityState.typeMismatch && node) {
    automaticErrorMessage = `Input must be a valid ${
      node.getAttribute('type') || 0
    }`
  }

  return automaticErrorMessage
}

/**
 * Process `<input>` `ValidityState`.
 *
 * @note `onValidate` will only be called if `ref` is a ref used by an
 *   `HTMLInputElement`.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/ValidityState
 */
export function useValidityState({
  explicitError = '',
  onValidate,
  patternExplanation,
  previouslyModified = false,
  ref,
}: HookProps): HookObject {
  const [customErrorMessage, setCustomErrorMessage] =
    useState<string>(explicitError)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [modified, setModified] = useState<boolean>(previouslyModified)
  // Because `valid` is used as dependency in the `useEffect` hooks, external
  // code can modify `ref`'s `ValidityState` and call `setValid` to trigger the
  // `useEffect` hooks.
  const [valid, setValid] = useState<boolean>(customErrorMessage === '')

  useEffect(() => {
    setModified(previouslyModified)
  }, [previouslyModified])

  // Apply `customErrorMessage` to the constraint validation API's custom
  // validity mechanism.
  //
  // @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLObjectElement/setCustomValidity
  useEffect(() => {
    if (!modified) return

    const node = ref?.current
    if (!node) return

    node.setCustomValidity(customErrorMessage)
  }, [customErrorMessage, modified, onValidate, ref])

  // Update `errorMessage`, using either `customErrorMessage` or a generated
  // message.
  useEffect(() => {
    if (!modified) return

    if (customErrorMessage !== '') {
      setErrorMessage(customErrorMessage)
      return
    }

    const automaticErrorMessage = errorMessageFromValidityState({
      node: ref?.current,
      patternExplanation,
    })
    setErrorMessage(automaticErrorMessage || '')
  }, [customErrorMessage, modified, onValidate, patternExplanation, ref, valid])

  // Trigger `onValidate` callback.
  useEffect(() => {
    if (!modified) return

    const htmlInputValid = ref?.current?.validity.valid
    if (onValidate && typeof htmlInputValid !== 'undefined')
      onValidate(htmlInputValid)
  }, [customErrorMessage, modified, onValidate, ref, valid])

  return {
    errorMessage,
    modified,
    setCustomErrorMessage,
    setModified,
    setValid,
  }
}
